import { send } from "../api.js";
import type { PrivateBookingSetup, PrivateBookingState } from "../api.js";

/**
 * Setting a student's booking payer up, and what a screen says about it.
 *
 * Two things live here: the prose for each state the Hub reports, and the one
 * seam where the booking system would actually be asked to do the work.
 *
 * **The Hub does not find or create Amelia customers.** Not here, not in the
 * page, not anywhere on this side. Amelia customer lookup, creation, duplicate
 * and race handling and the link back to this student are FastAPI's, and the
 * whole point of the exchange is that there is one implementation of them. So
 * the action below is a request to that service and nothing else — it sends no
 * payer fields, because sending them would make the browser the source of an
 * identity FastAPI is supposed to ask the Hub for.
 */

/**
 * The operation that performs the setup.
 *
 * One method, and the shape is the whole point: a student reference and a
 * retry key, and **nothing else**. No payer name, no email, no Amelia customer
 * id. FastAPI asks the Hub for the authoritative payer decision itself, which
 * is what stops the browser becoming a second source of an identity the Hub
 * already owns.
 */
export interface PrivateBookingSetupOperation {
  send: (portalStudentId: string, retryKey: string) => Promise<void>;
}

/**
 * The published operation, as WMF-SIte #42 froze it.
 *
 * `POST /api/v1/private-booking/students/{portal_student_id}/setup`, through
 * the Hub's staff proxy — the frozen contract marks it `StaffBearer`, so it
 * travels the same way every other staff mutation does and arrives with the
 * caller's own token rather than an ambient one.
 *
 * **No request body at all.** Not an empty object: nothing. Every field such a
 * body could carry is one the Hub is authoritative for and FastAPI asks the
 * Hub for directly.
 *
 * The response is deliberately not read. It carries the customer id, and
 * taking it from here would make the browser the thing that decides a student
 * is linked. What actually makes them linked is the row FastAPI writes back
 * through the Hub's internal link-back endpoint, so the screen re-reads the
 * Hub and believes that instead. It also means this side is unaffected if the
 * response shape ever changes.
 */
export const publishedPrivateBookingSetup: PrivateBookingSetupOperation = {
  send: async (portalStudentId, retryKey) => {
    await send(
      "POST",
      `/staff-api/api/v1/private-booking/students/${encodeURIComponent(portalStudentId)}/setup`,
      undefined,
      { "Idempotency-Key": retryKey },
    );
  },
};

/**
 * Whether the action is switched on. **It is** — WMF-SIte #42 is merged and
 * deployed, so the route answers in production.
 *
 * Kept as a nullable seam rather than collapsed into the implementation. It
 * cost one line to switch on and it would cost one line to switch off again,
 * which is the right shape for the only part of this screen that depends on
 * another service being present: if the backend is ever rolled back, an owner
 * gets the honest "waiting on the booking system" note instead of a 404 they
 * would read as the student's record being wrong.
 */
export const PRIVATE_BOOKING_SETUP_OPERATION: PrivateBookingSetupOperation | null =
  publishedPrivateBookingSetup;

/**
 * Ask the booking system to set this student's payer up.
 *
 * Throws while the operation is switched off. Callers should not reach that —
 * the action is offered only when `PRIVATE_BOOKING_SETUP_OPERATION` exists —
 * but a thrown error is the right failure for a mis-wired screen, rather than
 * resolving as though a payer had been created.
 */
export async function requestPrivateBookingSetup(
  portalStudentId: string,
  retryKey: string,
): Promise<void> {
  const operation = PRIVATE_BOOKING_SETUP_OPERATION;
  if (operation === null) {
    throw new Error("The booking system has no private booking setup operation yet.");
  }
  await operation.send(portalStudentId, retryKey);
}

export interface SetupCopy {
  /** The state in two or three words, for the value beside the label. */
  status: string;
  /** What it means and, where something is wrong, what to do about it. */
  detail: string;
  /** Whether the setup action applies to this student at all. */
  action: boolean;
}

/**
 * What each state says.
 *
 * The reason codes are the Hub's and are stable; this prose is not, and is
 * meant to be argued with. Two rules behind the wording as it stands:
 *
 * A correction says which field, because "needs details" on its own sends
 * somebody back to a form of nine fields to guess which one. And a link
 * conflict does not suggest a fix at all — the fix is deciding which payer a
 * child's existing lessons and credits belong to, and no wording should make
 * that look like a thing to click past.
 */
export function setupCopy(setup: PrivateBookingSetup): SetupCopy {
  if (setup.state === "linked") {
    return {
      status: "Ready",
      detail: "A booking payer is set up for this student's private lessons.",
      action: false,
    };
  }

  if (setup.state === "not_required") {
    return {
      status: "Not needed",
      detail:
        setup.reason === "school_not_configured"
          ? "School lessons are timetabled here and need no booking payer. This student's school is not set up as a payer, which matters only if they are ever booked an Amelia-backed lesson."
          : "School lessons are timetabled here and need no booking payer.",
      action: false,
    };
  }

  if (setup.state === "not_set_up") {
    return {
      status: "Not set up yet",
      detail:
        "This student can be set up for private booking. The booking system will reuse the payer's existing customer where there is one — a sibling's, for instance — rather than creating a second.",
      action: true,
    };
  }

  return { status: "Needs details", ...correction(setup), action: false };
}

function correction(setup: PrivateBookingSetup): { detail: string } {
  switch (setup.reason) {
    case "no_payer_on_record":
      return { detail: "Choose who pays for this student's lessons first." };
    case "no_payer_email":
      return { detail: "Add the payer's email address before setting up private booking." };
    case "payer_name_incomplete":
      return {
        detail:
          "Enter the payer's full name — first and last — before setting up private booking. Do not make a surname up: it becomes their name on every confirmation email the booking system sends them.",
      };
    case "school_not_configured":
      // Two different records are wrong here, and they are fixed in different
      // places. A null payer means the school is not named on the student at
      // all, so there is nothing yet for the School payers page to be asked
      // about — sending somebody there would waste the trip.
      return setup.payer === null
        ? {
            detail:
              "The school is set as the payer, but no school is named on this student's record. Add it, or record a different payer.",
          }
        : {
            detail:
              "This student's school is not set up as a payer. Set it up on the School payers page, or record a different payer.",
          };
    case "payer_link_conflict":
      return {
        detail:
          "This student is linked to a different booking customer than the payer their record names. Nothing will be changed automatically — existing lessons, credits and payments may belong to the linked customer, so somebody has to decide which is right.",
      };
    default:
      return { detail: "This student's record needs correcting before private booking is set up." };
  }
}

/** The label for the payer, or a note that there is not one. */
export function payerLabel(setup: PrivateBookingSetup): string {
  const kinds: Record<string, string> = { family: "Family", self: "Self", school: "School" };
  if (setup.payer === null) return "Nobody recorded";
  return `${kinds[setup.payer.kind] ?? setup.payer.kind} — ${setup.payer.display_name}`;
}

/**
 * Whether to offer the School payers page as the way to fix this.
 *
 * Only where a school is actually named. `school_not_configured` covers two
 * different broken records: a school named on the student that nobody has set
 * up as a payer, and the school payer chosen with no school named at all. The
 * first is fixed on the School payers page; the second has nothing for that
 * page to be asked about yet, and sending somebody there wastes the trip.
 *
 * This lives here rather than in the panel because the panel is where it went
 * wrong: `setupCopy` already drew the distinction and the rendered link undid
 * it, because a decision in JSX is a decision no test was reaching.
 */
export function linksToSchoolPayers(setup: PrivateBookingSetup): boolean {
  return setup.reason === "school_not_configured" && setup.payer !== null;
}

/** Whether the state is one the screen should draw attention to. */
export function isProblem(state: PrivateBookingState): boolean {
  return state === "needs_details";
}
